import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { updateUserInformation } from "../../utils/api";
import FocusedFlowPage from "../../components/FocusedFlowPage";
import { ADMIN_ROUTES } from "../../constants/routes";

const maroon = "#500000";

const EditUserConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get data from location state
  const { user, reason, comments, updatedData, originalData, returnTo } =
    location.state || {};

  const getChanges = () => {
    const changes = [];

    // Add null checks to prevent the error
    if (!updatedData || !originalData) {
      return changes;
    }

    Object.keys(updatedData).forEach((key) => {
      const current = updatedData[key];
      const original = originalData[key];

      // Handle array comparisons for multi-select fields
      let hasChanged = false;
      if (Array.isArray(current) && Array.isArray(original)) {
        hasChanged =
          current.length !== original.length ||
          !current.every((item, index) => item === original[index]);
      } else {
        hasChanged = current !== original;
      }

      if (hasChanged) {
        changes.push({
          field: key,
          original: Array.isArray(original)
            ? original.join(", ")
            : original || "Not set",
          updated: Array.isArray(current)
            ? current.join(", ")
            : current || "Not set",
        });
      }
    });

    return changes;
  };

  const changes = getChanges();

  // Don't render if data is not ready
  if (!updatedData || !originalData) {
    return (
      <FocusedFlowPage
        title="Confirm User Updates"
        maxWidth={700}
        actions={[
          <Button
            key="user-management"
            onClick={() => navigate(ADMIN_ROUTES.USER_MANAGEMENT)}
            variant="contained"
            sx={{ backgroundColor: maroon }}
          >
            Back to User Management
          </Button>,
        ]}
      >
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="error">
            No data found. Please go back to user management.
          </Typography>
        </Box>
      </FocusedFlowPage>
    );
  }

  const formatFieldName = (field) => {
    const fieldMap = {
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      department: "Departments",
      jobRole: "Job Roles",
      purposeForAccess: "Purpose for Access",
      admin: "Admin",
      tamuTeam: "TAMU Team",
    };
    return fieldMap[field] || field;
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      // Transform data to use plural field names for server
      const serverData = {
        ...updatedData,
        departments: updatedData.department || [],
        jobRoles: updatedData.jobRole || [],
        // Remove singular field names
        department: undefined,
        jobRole: undefined,
      };

      await updateUserInformation(user.userId, serverData, reason, comments);

      // Navigate back to user management with success message
      navigate(ADMIN_ROUTES.USER_MANAGEMENT, {
        state: {
          successMessage: "User information updated successfully!",
        },
      });
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Failed to update user information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(ADMIN_ROUTES.USER_EDIT, {
      state: {
        user,
        reason,
        comments,
        returnTo,
        step: "edit",
      },
    });
  };

  const handleCancel = () => {
    // Navigate back to the specified return path, or user profile, or user management
    if (returnTo) {
      navigate(returnTo);
    } else if (user && user.id) {
      navigate(ADMIN_ROUTES.USER_PROFILE(user.id));
    } else {
      // Fallback to user management if no user data
      navigate(ADMIN_ROUTES.USER_MANAGEMENT);
    }
  };

  return (
    <FocusedFlowPage
      title="You've Updated User Information"
      subtitle="The following information has been updated. Does this look correct?"
      onClose={handleCancel}
      maxWidth={680}
      actions={[
        <Button
          key="back"
          onClick={handleBack}
          variant="outlined"
          sx={{
            borderColor: "#ccc",
            color: "#666",
            "&:hover": {
              borderColor: "#999",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          GO BACK
        </Button>,
        <Button
          key="cancel"
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderColor: "#ccc",
            color: "#666",
            "&:hover": {
              borderColor: "#999",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: maroon,
            "&:hover": {
              backgroundColor: "#4a0000",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#666",
            },
            fontWeight: 600,
            px: 3,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "white" }} />
              Updating...
            </Box>
          ) : (
            "SAVE & UPDATE"
          )}
        </Button>,
      ]}
    >
      <Box sx={{ maxWidth: "600px", mx: "auto" }}>
        {/* Content */}
        <Paper sx={{ p: 4, mb: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {changes.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, color: maroon, fontWeight: 600 }}
              >
                Changes Made:
              </Typography>
              <List sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 1 }}>
                {changes.map((change, index) => (
                  <React.Fragment key={change.field}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              {formatFieldName(change.field)}:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "#666", mb: 0.5 }}
                            >
                              Previous: {change.original}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: maroon, fontWeight: 500 }}
                            >
                              Updated: {change.updated}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < changes.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "#666", fontStyle: "italic" }}
            >
              No changes were made to the user information.
            </Typography>
          )}

          {reason && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Reason for Edit:
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {reason}
              </Typography>
            </Box>
          )}

          {comments && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Comments:
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {comments}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </FocusedFlowPage>
  );
};

export default EditUserConfirmation;
